import React, { useState, useRef, useEffect, useContext } from "react";
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Typography,
  Paper,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContentText,
  DialogActions,
  DialogContent,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import PassportPageHeader from "../PassportPageHeader";
import { useGetToken } from "../../hooks/useGetToken";
import { logUserAction } from "../../utils/apiUtils";
import { CONSTANTS } from "../../CONSTANTS";
import Context from "../../context";
import axios from "axios";
import { Document, Page, pdfjs } from "react-pdf";
import { useGlobalStateContext } from "../../context/globalState";

// CRA/CloudFront/S3 SPA setups may rewrite unknown paths to index.html (text/html),
// which breaks PDF.js worker loading when it resolves to an in-app URL.
// Using a CDN-hosted worker avoids MIME-type and rewrite issues across environments.
const pdfWorkerSrc =
  process.env.REACT_APP_PDFJS_WORKER_SRC ||
  `https://cdn.jsdelivr.net/npm/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

pdfjs.GlobalWorkerOptions.workerSrc = pdfWorkerSrc;

interface TermsAndConditionProps {
  onSignOut: () => void;
}

const TermsAndCondition: React.FC<TermsAndConditionProps> = ({ onSignOut }) => {
  const {
    authState: { user },
  } = useContext(Context);

  const { isAgreed, setIsAgreed, isReview } = useGlobalStateContext();

  
  const { t } = useTranslation();
  const [isScrolledToEnd, setIsScrolledToEnd] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const [getToken] = useGetToken();
  const [loadingConsent, setLoadingConsent] = useState(true); // ✅ new
  const [consentRequired, setConsentRequired] = useState(false);
  const [isUnauthorized, setIsUnauthorized] = useState(false);
  const [privacyStatementURL, setPrivacyStatementURL] = useState<string | null>(
    null,
  );
  const [numPages, setNumPages] = useState<number>(0);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfError, setPdfError] = useState<string | null>(null);
  const [pageWidth, setPageWidth] = useState<number>(620);
  const [listStatementLanguages, setListStatementLanguages] = useState<
  { code: string; name: string }[]
>([]);
const [statementLanguage, setStatementLanguage] = useState<string>("");
const [folderPrefix, setFolderPrefix] = useState<string>("");
const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);
const pdfScale = Math.min(1.5, Math.max(1, 800 / Math.max(1, pageWidth)));

  // useEffect(() => {
  //   pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  //     "pdfjs-dist/build/pdf.worker.min.mjs",
  //     import.meta.url
  //   ).toString();
  // }, []);

  // ✅ WAIT FOR USER
  useEffect(() => {
    if (user?.username) {
      fetchConsentStatus();
    }
  }, [user]);

  // ✅ REVIEW MODE AUTO CHECK
  useEffect(() => {
    if (consentRequired && isReview) {
      setIsAgreed(true);
    }
  }, [consentRequired, isReview]);

  // SCROLL TRACK
  useEffect(() => {
    const handleScroll = () => {
      if (contentRef.current) {
        const { scrollTop, scrollHeight, clientHeight } = contentRef.current;
        setIsScrolledToEnd(scrollTop + clientHeight >= scrollHeight - 1); // Adjusted for floating-point precision issues
      }
    };

    const currentRef = contentRef.current;
    if (currentRef) {
      currentRef.addEventListener("scroll", handleScroll);
    }

    return () => {
      if (currentRef) {
        currentRef.removeEventListener("scroll", handleScroll);
      }
    };
  }, []);


  // ✅ CHECKBOX HANDLER
  const handleAgreementChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const checked = event.target.checked;

    if (!checked && isReview) {
      setShowConfirmationDialog(true);
      return;
    }

    setIsAgreed(checked);
  };


  /**
   * Fetches the available languages for the privacy statement based on the user's country code.
   * @param userCountryCode The country code of the user.
   * This function makes an API call to the backend to retrieve the available languages for the privacy statement.
   * It includes the user's country code as a `userCountry` query parameter in the request URL to get localized versions if available. The response is logged to the console for debugging purposes.
   */
  const handleCancelDisagree = () => {
    setShowConfirmationDialog(false);
  };

  const handleConfirmDisagree = async () => {
    setShowConfirmationDialog(false);

    try {
      const token = await getToken();
      await axios.post(
        `${process.env.REACT_APP_API_ENDPOINT}/v1/saveUserConsent`,
        {
          consentGiven: false,
          userId: user?.username,
          timestamp: new Date().toISOString(),
        },
        { headers: { Authorization: token } }
      );
      onSignOut();
    } catch (err) {
      console.error(err);
    }
  };
  // API CALLS
  const getAvailableLanguagesForPrivacyStatement = async (
    userCountryCode: string,
  ) => {
    // Get available languages for privacy statement from backend
    // Pass the user's country as the `userCountry` query parameter to get localized versions if available

    const token = await getToken();
    const config = { headers: { Authorization: token } };
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_ENDPOINT}/v1/privacyPolicy/availableLanguages?userCountry=${userCountryCode}`,
        config,
      );
      console.log("Available languages for privacy statement:", response.data);
      const availableLanguages = response.data?.availableLanguages || [];
      setListStatementLanguages(availableLanguages);
      const preferredLanguage = response.data?.preferredLanguage;
      const defaultLanguage =
        preferredLanguage && preferredLanguage.trim() !== ""
          ? preferredLanguage
          : availableLanguages[0] || "";
      setStatementLanguage(defaultLanguage);
      setFolderPrefix(response.data?.folderPrefix || "");
    } catch (error) {
      console.error(
        "Error fetching available languages for privacy statement:",
        error,
      );
    }
  };
     /**
   * Fetches the URL of the privacy statement PDF from the backend.
   * This function makes an API call to the backend to retrieve the URL of the privacy statement PDF.
   * The folderPrefix parameter is received from the getAvailableLanguagesForPrivacyStatement function and stored in component state.
   * The folderPrefix parameter can be used to specify a particular version of the privacy statement based on the user's country or language.
   * The response is set in the component state to be used for rendering the PDF document. The response is also logged to the console for debugging purposes.
   */
   const fetchPrivacyStatementURL = async () => {
    try {
      const token = await getToken();
      const config = { headers: { Authorization: token } };
      const privacyResponse = await axios.get(
        `${process.env.REACT_APP_API_ENDPOINT}/v1/privacyPolicy?folderPrefix=${folderPrefix}&language=${statementLanguage}`,
        config,
      );
      console.log("Privacy statement URL fetched:", privacyResponse.data);
      setPrivacyStatementURL(privacyResponse.data.presignedUrl);
    } catch (error) {
      console.error("Error fetching privacy statement URL:", error);
      setPrivacyStatementURL(null);
    } finally {
      setLoadingConsent(false);
    }
  };


  useEffect(() => {
    if (!consentRequired) return;
    // Wait until we have the necessary info to fetch the correct privacy statement
    if (!statementLanguage || !folderPrefix) return;
    setLoadingConsent(true);
    fetchPrivacyStatementURL();
  }, [statementLanguage, folderPrefix, consentRequired]); 


    const fetchConsentStatus = async () => {
      try {
        // Check if user was trying to access /admin
        const redirectUrl = window.sessionStorage.getItem("redirectUrl");
        if (redirectUrl && redirectUrl.startsWith("/admin")) {
          // Skip consent check for admin route
          setLoadingConsent(false);
          navigate(redirectUrl);
          window.sessionStorage.removeItem("redirectUrl");
          return;
        }

        const token = await getToken();
        const config = { headers: { Authorization: token } };
        const response = await axios.get(
          `${process.env.REACT_APP_API_ENDPOINT}/v1/getUserConsent?userId=${user?.username}`,
          config,
        );

        const consent = response.data?.consentGiven;

      setConsentRequired(true);

      if (consent) {
        setIsAgreed(true);

        await getAvailableLanguagesForPrivacyStatement(response.data?.country);

        if (!isReview) {
          navigate("/qualification");
        }
      } else {
        setIsAgreed(false);
        await getAvailableLanguagesForPrivacyStatement(
          response.data?.country || "",
        );
     
      }
      } catch (err: any) {
        console.error("Error fetching consent:", err);

        // Check if error is 403 Forbidden, 404 Not Found, or Network Error (unauthorized user blocked by API Gateway)
        const statusCode = err?.response?.status || err?.status;
        const isNetworkError =
          err?.code === "ERR_NETWORK" || err?.message === "Network Error";

        // Network errors on this endpoint usually mean the authorizer denied access (deleted/unauthorized user)
        if (statusCode === 403 || statusCode === 404 || isNetworkError) {
          setIsUnauthorized(true);
        } else {
          setConsentRequired(true); // fallback → show privacy statement for other errors
          await getAvailableLanguagesForPrivacyStatement(""); // Pass empty string to get default language and folder prefix
        }
      } finally {
        setLoadingConsent(false);
      }
    };

  const handleCloseApplication = async () => {
    if (isReview) {
      navigate(-1); // Navigate to the previous page in review mode
      return;
    }
    const token = await getToken();
    logUserAction(token, user?.username || "", CONSTANTS.USER_ACTION.LOGOUT);
    onSignOut(); // Call the onSignOut function passed as a prop
    navigate("/SignOut");
  };

  const handleContinue = async () => {
    if (!isAgreed && isReview) {
      setShowConfirmationDialog(true);
      return;
    }

    try {
      const token = await getToken();
      const config = {
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
      };
      console.log("isAgreed", isAgreed);
      await axios.post(
        `${process.env.REACT_APP_API_ENDPOINT}/v1/saveUserConsent`,
        { consentGiven: isAgreed, userId: user?.username },
        config,
      );
      if (isAgreed) {
        // Consent successfully given → navigate to stored URL or default to qualification
        const redirectUrl = window.sessionStorage.getItem("redirectUrl");
        const targetUrl = redirectUrl || "/qualification";
        navigate(targetUrl);
        if (redirectUrl) {
          window.sessionStorage.removeItem("redirectUrl");
        }
      }
    } catch (error) {
      console.error("Error sending agreement status:", error);
    }
  };

  const handlePdfLoadSuccess = ({
    numPages: loadedPages,
  }: {
    numPages: number;
  }) => {
    setNumPages(loadedPages);
    setPdfLoading(false);
    setPdfError(null);
  };

  const handlePdfLoadError = (error: Error) => {
    console.error("Error loading privacy statement PDF:", error);
    setPdfLoading(false);
    setPdfError(t("passportPage.privacyStatements.load_error"));
  };

  useEffect(() => {
    if (privacyStatementURL) {
      setPdfLoading(true);
      setPdfError(null);
      setNumPages(0);
    }
  }, [privacyStatementURL]);

  useEffect(() => {
    const updateWidth = () => {
      const el = contentRef.current;
      if (!el) return;
      // subtract small padding to avoid horizontal scroll
      const computed = Math.max(300, el.clientWidth - 12);
      setPageWidth(computed);
    };

    updateWidth();

    let ro: ResizeObserver | null = null;
    try {
      ro = new ResizeObserver(() => updateWidth());
      if (contentRef.current) ro.observe(contentRef.current);
    } catch (e) {
      // ResizeObserver may not be available in some test envs — fall back to window resize
      window.addEventListener("resize", updateWidth);
    }

    return () => {
      if (ro && contentRef.current) ro.disconnect();
      window.removeEventListener("resize", updateWidth);
    };
  }, [contentRef, privacyStatementURL]);

  // ✅ Loading state
  if (loadingConsent) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

   // Show unauthorized message for deleted/unauthorized users
   if (isUnauthorized) {
    return (
      <>
        <PassportPageHeader onSignOut={onSignOut} />
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="80vh"
          flexDirection="column"
        >
          <Typography
            variant="h5"
            sx={{
              color: "#D32F2F",
              fontWeight: 600,
              marginBottom: 2,
            }}
          >
            {t("adminDashboard.nonAuthorizedMessage")}
          </Typography>
          <Button
            variant="contained"
            onClick={handleCloseApplication}
            sx={{
              textTransform: "none",
              fontSize: "14px",
              padding: "8px 24px",
              borderRadius: "8px",
              backgroundColor: "#2450F5",
              color: "#FFFFFF",
              marginTop: 2,
            }}
          >
            {t("passportPage.privacyStatements.close_application")}
          </Button>
        </Box>
      </>
    );
  }


  if (!consentRequired && !isReview) return null;

  return (
    <>
      <PassportPageHeader onSignOut={onSignOut} />
  
      <Paper
        elevation={3}
        sx={{
          maxHeight: "90vh",
          padding: "11px",
          width: "calc(100% - 16px)",
          maxWidth: { xs: "100%", sm: "700px", md: "900px" },
          margin: "auto",
          marginTop: "10px",
          borderRadius: "8px",
          boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
          textAlign: "center",
          fontFamily: "Arial, sans-serif",
          backgroundColor: "#F1F4FD",
        }}
      >
        <Box
          sx={{
            padding: "11px",
            backgroundColor: "#FFFFFF",
          }}
        >
    <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mb: 2,
              flexWrap: "wrap",
            }}
          >
            <img
              src="/icon_globe.svg"
              alt="globe icon"
              style={{
                width: "14px",
                height: "14px",
                objectFit: "cover",
              }}
            />
            <Typography
              variant="body2"
              component="label"
              htmlFor="statement-language-select"
              sx={{
                fontFamily: "Inter, sans-serif",
                fontSize: "12px",
                fontWeight: 600,
                color: "#3D3D3D",
                whiteSpace: "nowrap",
                textAlign: "right",
                ml: 0.5,
              }}
            >
              {t("passportPage.privacyStatements.language_label")}
            </Typography>

            <Box
              component="select"
              id="statement-language-select"
              value={statementLanguage || ""}
              onChange={(e) =>
                setStatementLanguage((e.target as HTMLSelectElement).value)
              }
              sx={{
                minWidth: 10,
                height: 20,
                px: 1,
                border: "none",
                color: "#3D3D3D",
                fontSize: "12px",
                fontFamily: "Inter, sans-serif",
                outline: "none",
              }}
            >
              {listStatementLanguages.map((lang) => (
                <option key={lang.code} value={lang.code}>
                  {lang.name}
                </option>
              ))}
            </Box>
          </Box>
          <Typography
            variant="h6"
            sx={{
              font: "normal normal 600 12px/17px Inter",
              letterSpacing: "0px",
              textAlign: "left",
              fontFamily: "Inter, sans-serif",
              fontWeight: 600,
              fontSize: "12px",
              lineHeight: "17px",
              color: "#3D3D3D",
              opacity: 1,
              marginBottom: "16px",
            }}
          >
            {t("passportPage.privacyStatements.consent_title")}
          </Typography>
          <Box
            ref={contentRef}
            sx={{
              maxHeight: "60vh",
              overflowY: "auto",
              overflowX: "auto",
              border: "1px solid #ccc",
              borderRadius: "8px",
              padding: "11px",
              textAlign: "left",
              fontSize: "14px",
              lineHeight: "1.6",
              backgroundColor: "#ffffff",
            }}
          >
            {privacyStatementURL ? (
              <>
                {pdfLoading && (
                  <Box display="flex" justifyContent="center" minHeight="120px">
                    <CircularProgress size={24} />
                  </Box>
                )}
                {pdfError && (
                  <Typography variant="body2" sx={{ color: "#D32F2F", mb: 2 }}>
                    {pdfError}
                  </Typography>
                )}
  
                {privacyStatementURL && (
                  <Document
                    file={privacyStatementURL}
                    onLoadSuccess={handlePdfLoadSuccess}
                    onLoadError={handlePdfLoadError}
                    loading={null}
                  >
                    {numPages > 0 &&
                      Array.from({ length: numPages }, (_, index) => (
                        <Page
                          key={`page_${index + 1}`}
                          pageNumber={index + 1}
                          width={Math.floor(pageWidth)}
                          scale={pdfScale}
                          renderAnnotationLayer={false}
                          renderTextLayer={false}
                        />
                      ))}
                  </Document>
                )}
              </>
            ) : (
              <Typography variant="body2" sx={{ color: "#666666" }}>
                {t("passportPage.privacyStatements.no_document")}
              </Typography>
            )}
          </Box>
  
          {/* ✅ CHECKBOX FIXED */}
          <Box
            sx={{
              position: "sticky",
              bottom: 0,
              backgroundColor: "#FFFFFF",
              padding: "16px",
              borderTop: "1px solid #ccc",
              display: "flex",
              flexDirection: "column",
              gap: 2,
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={!!isAgreed}   // ✅ FIX: controlled checkbox
                  onChange={handleAgreementChange}
                  sx={{ color: isAgreed ? "#2450F5" : "#2350F5" }}
                />
              }
              label={t("passportPage.privacyStatements.agree_checkbox")}
              sx={{
                fontSize: "14px",
                wordBreak: "break-word",
              }}
            />
  
            {/* BUTTONS - UNCHANGED */}
            <Box
              sx={{
                display: "flex",
                gap: 2,
                justifyContent: "flex-end",
              }}
            >
                <Button
                variant="outlined"
                onClick={handleCloseApplication}
                sx={{
                  textTransform: "none",
                  fontSize: "14px",
                  padding: "8px 16px",
                  borderRadius: "8px",
                  color: "#2450F5",
                }}
                >
                {isReview
                  ? t("adminDashboard.form.button.cancel")
                  : t("passportPage.privacyStatements.close_application")}
                </Button>
              <Button
                variant="contained"
                onClick={handleContinue}
                disabled={!isAgreed && isReview}
                sx={{
                  textTransform: "none",
                  fontSize: "14px",
                  padding: "8px 16px",
                  borderRadius: "8px",
                  backgroundColor: "#2450F5",
                  color: "#FFFFFF",
                }}
              >
                {t("passportPage.privacyStatements.continue")}
              </Button>
            </Box>
          </Box>
        </Box>
      </Paper>
  
      {/* DIALOG - UPDATED STYLING */}
      <Dialog
        open={showConfirmationDialog}
        onClose={handleCancelDisagree}
        PaperProps={{
          sx: {
        borderRadius: "12px",
        padding: "16px",
        maxWidth: "400px",
        width: "100%",
          },
        }}
      >
        <DialogTitle
          sx={{
        fontSize: "18px",
        fontWeight: "bold",
        textAlign: "center",
        paddingBottom: "8px",
          }}
        >
          {t("passportPage.privacyStatements.confirmation_title")}
        </DialogTitle>
        <DialogContent>
          <DialogContentText
        sx={{
          fontSize: "14px",
          color: "#666666",
          textAlign: "center",
          marginBottom: "16px",
        }}
          >
        {t("passportPage.privacyStatements.confirmation_message")}
          </DialogContentText>
        </DialogContent>
        <DialogActions
          sx={{
        justifyContent: "center",
        gap: "16px",
        padding: "8px 16px",
          }}
        >
          <Button
        onClick={handleCancelDisagree}
        variant="outlined"
        sx={{
          textTransform: "none",
          fontSize: "14px",
          padding: "8px 16px",
          borderRadius: "8px",
          color: "#2450F5",
          borderColor: "#2450F5",
        }}
          >
        {t("common.no")}
          </Button>
          <Button
        onClick={handleConfirmDisagree}
        variant="contained"
        sx={{
          textTransform: "none",
          fontSize: "14px",
          padding: "8px 16px",
          borderRadius: "8px",
          backgroundColor: "#2450F5",
          color: "#FFFFFF",
          "&:hover": {
            backgroundColor: "#1E3A8A",
          },
        }}
          >
        {t("common.yes")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default TermsAndCondition;